
import React, { useState, useRef } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Col, Row, Card, Button, Tooltip, OverlayTrigger } from '@themesberg/react-bootstrap';
import { LiveProvider, LiveEditor, LiveError, LivePreview } from 'react-live';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import Code from "./Code";

import themeStyle from "../assets/syntax-themes/ghcolors.json";

function shallowEqualScopes(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => a[k] === b[k]);
}

/**
 * react-live's LiveProvider uses `scope !== prevScope` (reference). A fresh
 * `{ ...scope, process }` every render re-transpiles forever and freezes the UI.
 * Reuse one object and only mutate when scope contents actually change.
 */
function useStableLiveScope(baseScope) {
  const mergedRef = useRef(null);
  if (!mergedRef.current) {
    mergedRef.current = {};
  }

  const processStubRef = useRef(null);
  if (!processStubRef.current) {
    processStubRef.current = {
      env: {
        NODE_ENV:
          typeof process !== "undefined" &&
          process.env &&
          process.env.NODE_ENV
            ? process.env.NODE_ENV
            : "development",
      },
    };
  }

  const prevSnapshot = useRef(null);
  if (
    !prevSnapshot.current ||
    !shallowEqualScopes(prevSnapshot.current, baseScope)
  ) {
    prevSnapshot.current = { ...baseScope };
    const m = mergedRef.current;
    Object.keys(m).forEach((k) => {
      if (k !== "process") delete m[k];
    });
    Object.assign(m, baseScope);
    m.process = processStubRef.current;
  }

  return mergedRef;
}

export default (props) => {
  const { language = "jsx", scope = {}, imports = null, maxHeight = 'none' } = props;
  const [code, setCode] = useState(props.code);
  const [copied, setCopied] = useState(false);
  const noInline = code.includes('render(');
  const liveScopeRef = useStableLiveScope(scope);

  const handleCodeChange = (newCode) => {
    setCode(newCode);
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <LiveProvider noInline={noInline} code={code} language={language} theme={themeStyle} scope={liveScopeRef.current}>
      <Row>
        <Col xs={12} className="mb-4">
          <Card>
            <Card.Body style={{ maxHeight: maxHeight }}>
              <LivePreview />
            </Card.Body>
          </Card>
        </Col>
        {imports ? (
          <Col xs={12} className="mb-4">
            <Code code={imports} />
          </Col>
        ) : null}
        <Col xs={12} className="mb-4">
          <LiveError className="alert alert-danger" />

          <span className="fs-7 mb-2 d-block text-gray-600">
            <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={<Tooltip>You can edit the code below and the changes will be seen in the example above.</Tooltip>}>
              <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
            </OverlayTrigger>
            Live React Code Editor
          </span>

          <Card>
            <Card.Body className="position-relative">

              <LiveEditor onChange={handleCodeChange} className="live-editor" />

              {copied ? <span className="text-success copy-code-text">Copied</span> : null}

              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="top"
                overlay={<Tooltip>Copy to clipboard</Tooltip>}
              >
                <CopyToClipboard text={code} onCopy={handleCopy}>
                  <Button size="sm" variant="primary" className="copy-code-button">Copy</Button>
                </CopyToClipboard>
              </OverlayTrigger>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </LiveProvider>
  );
};
